import { memo, useState, useCallback, useId } from 'react';
import {
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Dialog,
  DialogContent,
  DialogTitle,
  Tooltip,
  Button,
} from '@mui/material';
import {
  Close as CloseIcon,
  FileDownloadOutlined as DownloadIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import { formatFileSize } from '../../utils/formatters';
import { triggerBrowserFileDownload } from '../../utils/fileDownload';
import EllipsisTooltip from './EllipsisTooltip';

// ─── PDF icon (provided design) ──────────────────────────────────────────────
const PdfSvgIcon = () => (
  <svg
    width="38"
    height="45"
    viewBox="0 0 38 45"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.63428 44.0238C2.33464 44.0238 0.463867 42.153 0.463867 39.8534V4.63412C0.463867 2.33448 2.33464 0.463707 4.63428 0.463707H26.6864L36.6096 10.3869V39.8534C36.6096 42.153 34.7389 44.0238 32.4392 44.0238H4.63428Z"
      fill="#F9F8F6"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M26.8784 7.62939e-05H4.63404C2.07485 7.62939e-05 0 2.07567 0 4.63411V39.8534C0 42.4126 2.07485 44.4874 4.63404 44.4874H32.439C34.9982 44.4874 37.073 42.4126 37.073 39.8534V10.1954L26.8784 7.62939e-05ZM26.4939 0.927327L36.1465 10.5792V39.8534C36.1465 41.8972 34.4835 43.5609 32.439 43.5609H4.63404C2.5895 43.5609 0.927251 41.8972 0.927251 39.8534V4.63411C0.927251 2.59032 2.5895 0.927327 4.63404 0.927327H26.4939Z"
      fill="#DEDED9"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M29.4268 27.8043H16.9148C16.1473 27.8043 15.5247 28.4269 15.5247 29.1944C15.5247 29.9627 16.1473 30.5853 16.9148 30.5853H29.4268C30.1943 30.5853 30.8177 29.9627 30.8177 29.1944C30.8177 28.4269 30.1943 27.8043 29.4268 27.8043ZM29.4268 33.3656H16.9148C16.1473 33.3656 15.5247 33.9882 15.5247 34.7557C15.5247 35.524 16.1473 36.1466 16.9148 36.1466H29.4268C30.1943 36.1466 30.8177 35.524 30.8177 34.7557C30.8177 33.9882 30.1943 33.3656 29.4268 33.3656ZM29.8904 22.2438H7.18311C6.41631 22.2438 5.79297 22.8664 5.79297 23.6339C5.79297 24.4022 6.41631 25.0248 7.18311 25.0248H29.8904C30.6579 25.0248 31.2805 24.4022 31.2805 23.6339C31.2805 22.8664 30.6579 22.2438 29.8904 22.2438Z"
      fill="#D4D4CF"
    />
    <mask
      id="mask0_22796_4589"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="38"
      height="17"
    >
      <rect y="0.000930786" width="37.0732" height="16.6814" fill="white" />
    </mask>
    <g mask="url(#mask0_22796_4589)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.63428 44.0239C2.33464 44.0239 0.463867 42.1531 0.463867 39.8535V4.63423C0.463867 2.33459 2.33464 0.463821 4.63428 0.463821H26.6864L36.6096 10.387V39.8535C36.6096 42.1531 34.7389 44.0239 32.4392 44.0239H4.63428Z"
        fill="#C7464E"
      />
    </g>
    <mask
      id="mask1_22796_4589"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="0"
      y="0"
      width="38"
      height="17"
    >
      <rect y="0.000930786" width="37.0732" height="16.6814" fill="white" />
    </mask>
    <g mask="url(#mask1_22796_4589)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M26.8784 0.000198364H4.63404C2.07485 0.000198364 0 2.07579 0 4.63423V39.8535C0 42.4127 2.07485 44.4875 4.63404 44.4875H32.439C34.9982 44.4875 37.073 42.4127 37.073 39.8535V10.1955L26.8784 0.000198364ZM26.4939 0.927449L36.1465 10.5793V39.8535C36.1465 41.8973 34.4835 43.561 32.439 43.561H4.63404C2.5895 43.561 0.927251 41.8973 0.927251 39.8535V4.63423C0.927251 2.59044 2.5895 0.927449 4.63404 0.927449H26.4939Z"
        fill="#B44149"
      />
    </g>
    <mask
      id="mask2_22796_4589"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="26"
      y="0"
      width="12"
      height="17"
    >
      <path
        d="M26.8789 0.000930786L37.073 10.1955V13.4392V16.6823H26.8784V0.000930786H26.8789Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask2_22796_4589)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M28.2085 8.80785L37.1787 17.7786V8.80785H28.2085Z"
        fill="#B44149"
      />
    </g>
    <mask
      id="mask3_22796_4589"
      style={{ maskType: 'luminance' }}
      maskUnits="userSpaceOnUse"
      x="26"
      y="0"
      width="12"
      height="17"
    >
      <path
        d="M26.8789 0.000930786L37.073 10.1955V13.4392V16.6823H26.8784V0.000930786H26.8789Z"
        fill="white"
      />
    </mask>
    <g mask="url(#mask3_22796_4589)">
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M37.9995 10.1955H31.5125C28.9525 10.1955 26.8784 8.11993 26.8784 5.56074V-0.926315H37.9995V10.1955Z"
        fill="#E49E9F"
      />
    </g>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M6.32881 6.73485V8.10428H6.77691C6.9699 8.10428 7.13405 8.03477 7.27159 7.8965C7.40838 7.75749 7.47715 7.59333 7.47715 7.4033C7.47715 7.18664 7.40025 7.01805 7.24497 6.89826C7.09117 6.77921 6.87008 6.71932 6.58613 6.71932L6.32881 6.73485ZM5.20117 5.77432H7.02905C7.52078 5.77432 7.91711 5.91777 8.2188 6.20393C8.51975 6.4901 8.67134 6.86721 8.67134 7.33453C8.67134 7.64066 8.59666 7.93199 8.44877 8.2078C8.30088 8.48361 8.07757 8.68917 7.77884 8.82449C7.48011 8.96055 7.225 9.03153 7.01279 9.03671L6.34508 9.05298V11.1219H5.20117V5.77432ZM10.7403 6.78439V10.0874H11.1965C11.669 10.0874 12.0565 9.95878 12.3604 9.70072C12.6643 9.44192 12.8166 9.01157 12.8166 8.40967C12.8166 8.05918 12.7441 7.74861 12.5977 7.47872C12.4506 7.20883 12.2384 7.02545 11.9596 6.92858C11.6816 6.83245 11.4154 6.78439 11.1632 6.78439H10.7403ZM9.57936 5.77432H11.4035C12.1829 5.77432 12.81 6.02055 13.2854 6.51154C13.7601 7.00326 13.999 7.65101 13.999 8.45477C13.999 9.26002 13.7535 9.90628 13.2625 10.3936C12.773 10.8794 12.1208 11.1219 11.3059 11.1219H9.57936V5.77432Z"
      fill="#FFFFFE"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M14.9062 5.77454H17.9155V6.80902H16.0506V7.90001H17.9155V8.94286H16.0579V11.1217H14.9062V5.77454Z"
      fill="#FFFFFE"
    />
    <rect
      x="5.56152"
      y="27.8043"
      width="7.4"
      height="8.3"
      rx="3.7"
      fill="#DEDED9"
    />
  </svg>
);

// ─── Generic document icon for non-PDF file types ────────────────────────────
const DocumentIcon = memo(({ bandColor = '#9ca3af', label = '' }) => {
  const uid = useId().replace(/:/g, '');
  const clipId = `fac_clip_${uid}`;

  return (
    <svg
      width="38"
      height="45"
      viewBox="0 0 38 45"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <clipPath id={clipId}>
          <rect y="0" width="38" height="16.68" />
        </clipPath>
      </defs>
      {/* Document body */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.634 44.024C2.335 44.024 0.464 42.153 0.464 39.854V4.634C0.464 2.335 2.335 0.464 4.634 0.464H26.686L36.61 10.387V39.854C36.61 42.153 34.739 44.024 32.44 44.024H4.634Z"
        fill="#F9F8F6"
      />
      {/* Outline */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M26.878 0H4.634C2.075 0 0 2.076 0 4.634V39.854C0 42.413 2.075 44.488 4.634 44.488H32.44C34.998 44.488 37.073 42.413 37.073 39.854V10.195L26.878 0ZM26.494 0.927L36.146 10.579V39.854C36.146 41.897 34.483 43.561 32.44 43.561H4.634C2.59 43.561 0.927 41.897 0.927 39.854V4.634C0.927 2.59 2.59 0.927 4.634 0.927H26.494Z"
        fill="#DEDED9"
      />
      {/* Content lines */}
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M29.89 22.244H7.183C6.416 22.244 5.793 22.866 5.793 23.634C5.793 24.402 6.416 25.025 7.183 25.025H29.89C30.658 25.025 31.281 24.402 31.281 23.634C31.281 22.866 30.658 22.244 29.89 22.244Z"
        fill="#D4D4CF"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M29.427 27.804H16.915C16.147 27.804 15.525 28.427 15.525 29.194C15.525 29.963 16.147 30.585 16.915 30.585H29.427C30.194 30.585 30.817 29.963 30.817 29.194C30.817 28.427 30.194 27.804 29.427 27.804Z"
        fill="#D4D4CF"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M29.427 33.366H16.915C16.147 33.366 15.525 33.988 15.525 34.756C15.525 35.524 16.147 36.147 16.915 36.147H29.427C30.194 36.147 30.817 35.524 30.817 34.756C30.817 33.988 30.194 33.366 29.427 33.366Z"
        fill="#D4D4CF"
      />
      {/* Bottom-left accent (matches PDF layout) */}
      <rect
        x="5.56"
        y="27.804"
        width="7.4"
        height="8.3"
        rx="3.7"
        fill="#DEDED9"
      />
      {/* Colour band (clipped to top) */}
      <g clipPath={`url(#${clipId})`}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4.634 44.024C2.335 44.024 0.464 42.153 0.464 39.854V4.634C0.464 2.335 2.335 0.464 4.634 0.464H26.686L36.61 10.387V39.854C36.61 42.153 34.739 44.024 32.44 44.024H4.634Z"
          fill={bandColor}
        />
      </g>
      {/* Fold corner (lighter shade) */}
      <g clipPath={`url(#${clipId})`}>
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M38 10.196H31.513C28.953 10.196 26.878 8.12 26.878 5.561V-0.927H38V10.196Z"
          fill="rgba(255,255,255,0.28)"
        />
      </g>
      {/* Extension label */}
      {label ? (
        <text
          x="3.5"
          y="11.8"
          fontSize="5"
          fontWeight="700"
          fill="white"
          fontFamily="Arial, Helvetica, sans-serif"
          letterSpacing="0.4"
        >
          {label}
        </text>
      ) : null}
    </svg>
  );
});

// ─── File-type configuration ──────────────────────────────────────────────────
const FILE_TYPE_CONFIG = {
  pdf: { bandColor: '#C7464E', label: 'PDF', previewType: 'pdf' },
  png: { bandColor: '#7c3aed', label: 'PNG', previewType: 'image' },
  jpg: { bandColor: '#7c3aed', label: 'JPG', previewType: 'image' },
  jpeg: { bandColor: '#7c3aed', label: 'JPEG', previewType: 'image' },
  gif: { bandColor: '#7c3aed', label: 'GIF', previewType: 'image' },
  webp: { bandColor: '#7c3aed', label: 'WEBP', previewType: 'image' },
  bmp: { bandColor: '#7c3aed', label: 'BMP', previewType: 'image' },
  svg: { bandColor: '#0891b2', label: 'SVG', previewType: 'image' },
  doc: { bandColor: '#2563eb', label: 'DOC', previewType: 'none' },
  docx: { bandColor: '#2563eb', label: 'DOCX', previewType: 'none' },
  xls: { bandColor: '#059669', label: 'XLS', previewType: 'none' },
  xlsx: { bandColor: '#059669', label: 'XLSX', previewType: 'none' },
  csv: { bandColor: '#059669', label: 'CSV', previewType: 'none' },
  ppt: { bandColor: '#d97706', label: 'PPT', previewType: 'none' },
  pptx: { bandColor: '#d97706', label: 'PPTX', previewType: 'none' },
  // Plain text renders directly in a browser tab/iframe — no viewer needed,
  // unlike doc/xlsx/ppt above which have no native browser renderer at all.
  txt: { bandColor: '#6b7280', label: 'TXT', previewType: 'text' },
  // Only formats the browser's own <video> element can actually decode —
  // keep in sync with EXTENSION_MIME_TYPES in utils/fileDownload.js.
  mp4: { bandColor: '#0f766e', label: 'MP4', previewType: 'video' },
  m4v: { bandColor: '#0f766e', label: 'M4V', previewType: 'video' },
  webm: { bandColor: '#0f766e', label: 'WEBM', previewType: 'video' },
  ogv: { bandColor: '#0f766e', label: 'OGV', previewType: 'video' },
  mov: { bandColor: '#0f766e', label: 'MOV', previewType: 'video' },
};

const DEFAULT_CONFIG = {
  bandColor: '#6b7280',
  label: 'FILE',
  previewType: 'none',
};

const getFileConfig = fileName => {
  if (!fileName) return DEFAULT_CONFIG;
  const ext = fileName.split('.').pop().toLowerCase();
  return (
    FILE_TYPE_CONFIG[ext] ?? {
      ...DEFAULT_CONFIG,
      label: ext.toUpperCase().slice(0, 4),
    }
  );
};

const getDisplaySize = fileSize => {
  if (fileSize === null || fileSize === undefined) return null;
  if (typeof fileSize === 'string') return fileSize;
  if (typeof fileSize === 'number') {
    if (fileSize <= 0) return '0 Bytes';
    const k = 1024;
    const units = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.min(
      Math.floor(Math.log(fileSize) / Math.log(k)),
      units.length - 1
    );
    return `${Math.round(fileSize / Math.pow(k, i))} ${units[i]}`;
  }
  return null;
};

// ─── Icon button (view / download) ───────────────────────────────────────────
const ActionButton = memo(({ title, onClick, disabled = false, children }) => (
  <Tooltip title={title} arrow>
    <span>
      <IconButton
        size="small"
        onClick={onClick}
        disabled={disabled}
        sx={{
          width: 32,
          height: 32,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          color: 'text.secondary',
          '&:hover': {
            bgcolor: 'primary.main',
            borderColor: 'primary.main',
            color: 'white',
          },
          '&.Mui-disabled': {
            opacity: 0.5,
          },
          transition: 'all 0.15s ease',
        }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
));

// ─── File viewer dialog ───────────────────────────────────────────────────────
const FileViewerDialog = memo(({ open, onClose, fileData, previewType }) => {
  const fileName = fileData?.fileName ?? 'File';
  const fileUrl = fileData?.fileUrl ?? '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 13400 }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: 'hidden',
          height: 'calc(100% - 64px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: 'text.primary' }}
        >
          {fileName}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          bgcolor: '#f1f3f4',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {previewType === 'pdf' && (
          <Box
            component="object"
            data={fileUrl}
            type="application/pdf"
            title={fileName}
            sx={{
              display: 'block',
              width: '100%',
              flex: 1,
              minHeight: 0,
              border: 'none',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
              Unable to preview this PDF in the browser.
            </Typography>
          </Box>
        )}

        {previewType === 'image' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: 0,
              p: 3,
            }}
          >
            <Box
              component="img"
              src={fileUrl}
              alt={fileName}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                objectFit: 'contain',
                borderRadius: 1,
                boxShadow: 3,
              }}
            />
          </Box>
        )}

        {previewType === 'video' && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              minHeight: 0,
              p: 3,
            }}
          >
            <Box
              component="video"
              src={fileUrl}
              controls
              autoPlay={false}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                borderRadius: 1,
                boxShadow: 3,
              }}
            >
              Unable to preview this video in the browser.
            </Box>
          </Box>
        )}

        {previewType === 'text' && (
          <Box
            component="iframe"
            src={fileUrl}
            title={fileName}
            sx={{
              display: 'block',
              width: '100%',
              flex: 1,
              minHeight: 0,
              border: 'none',
              bgcolor: 'white',
            }}
          />
        )}

        {previewType === 'none' && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flex: 1,
              p: 4,
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Preview is not available for this file type.
            </Typography>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              href={fileUrl}
              download={fileName}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Download to view
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
});

FileViewerDialog.displayName = 'FileViewerDialog';

export { FileViewerDialog };

// ─── Main component ───────────────────────────────────────────────────────────
/**
 * Props
 * ─────
 * isLogoVisible  boolean   Show the file-type icon on the left (default true).
 * fileData       object    { fileName, fileSize, fileUrl }
 * onView         function  Optional. If provided, called instead of opening the
 *                          internal preview dialog (use for authenticated endpoints).
 * onDownload     function  Optional. If provided, called instead of the default
 *                          anchor-click download (use for authenticated endpoints).
 * isLoading      boolean   Disables both action buttons and shows a spinner while
 *                          a parent async operation is in progress.
 */
const FileAttachmentCard = memo(
  ({
    isLogoVisible = true,
    fileData = {},
    onView,
    onDownload,
    isLoading = false,
  }) => {
    const [dialogOpen, setDialogOpen] = useState(false);

    const { fileName = '', fileSize, fileUrl = '' } = fileData;
    const config = getFileConfig(fileName);
    const displaySize = getDisplaySize(fileSize);
    // Only formats the browser can actually render inline (PDF, images,
    // video, plain text) open a preview — everything else (docx, xlsx, ppt,
    // …) has no in-browser renderer, so "View" falls back to downloading the
    // file instead of opening a broken/blank tab.
    const canPreview = config.previewType !== 'none';

    const handleViewClick = useCallback(() => {
      if (onView) {
        onView();
      } else {
        setDialogOpen(true);
      }
    }, [onView]);

    const handleClose = useCallback(() => setDialogOpen(false), []);

    const handleDownloadClick = useCallback(async () => {
      if (onDownload) {
        onDownload();
        return;
      }
      if (!fileUrl) return;
      try {
        const response = await fetch(fileUrl);
        const blob = await response.blob();
        await triggerBrowserFileDownload(blob, fileName || 'download');
      } catch (err) {
        console.error('File download failed:', err);
      }
    }, [onDownload, fileUrl, fileName]);

    return (
      <>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            minWidth: 170,
          }}
        >
          {/* File type icon */}
          {isLogoVisible && (
            <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
              {config.label === 'PDF' ? (
                <PdfSvgIcon />
              ) : (
                <DocumentIcon
                  bandColor={config.bandColor}
                  label={config.label}
                />
              )}
            </Box>
          )}

          {/* File name  +  size · view · download  */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <EllipsisTooltip
              title={fileName || 'Untitled'}
              style={{
                fontWeight: 600,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100px',
              }}
              // Rows rendered inside the "+N more" popover (AttachmentsField's
              // Popper sits at zIndex 13200, to clear a form modal's sticky
              // footer) need a higher zIndex than the tooltip's own default
              // (10003) — otherwise it renders behind that popover and never
              // actually becomes visible on hover.
              PopperProps={{ sx: { zIndex: 13300 } }}
            >
              {fileName || 'Untitled'}
            </EllipsisTooltip>

            {/* Size + action buttons on the same line */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                mt: 0.25,
              }}
            >
              {displaySize && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', lineHeight: 1, flexShrink: 0 }}
                >
                  {displaySize}
                </Typography>
              )}
              <ActionButton
                title={canPreview ? 'View file' : 'Preview not available.'}
                onClick={canPreview ? handleViewClick : handleDownloadClick}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <ViewIcon sx={{ fontSize: 16 }} />
                )}
              </ActionButton>
              <ActionButton
                title="Download file"
                onClick={handleDownloadClick}
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <DownloadIcon sx={{ fontSize: 16 }} />
                )}
              </ActionButton>
            </Box>
          </Box>
        </Box>

        {/* Only render the dialog when onView is not overriding it */}
        {!onView && (
          <FileViewerDialog
            open={dialogOpen}
            onClose={handleClose}
            fileData={fileData}
            previewType={config.previewType}
          />
        )}
      </>
    );
  }
);

export default FileAttachmentCard;
